import { postSubscriptionSettingsChanges } from 'client/api/subscriptionManagement'
import { DocumentTypes } from 'client/Interfaces/Draft/DraftInterface'
import { ModelInterface } from 'client/Interfaces/Settings/ModelInterface'
import { StateInterface } from 'client/Interfaces/StateInterface'
import { getCurrencies, getInvoiceTypes } from 'client/Other/Configs'
import { DEFAULT_UNITS } from 'client/reducers/settings'

import * as api from '../api/settings'
import { SettingsInterface } from '../Interfaces/Settings/SettingsInterface'
import { Immutable } from '../Other/Immutable'
import { l } from '../Other/Translation'
import { actionCreatorAsync, AsyncAction } from './index'
import { getSubscriptionSettings } from './subscriptionManagement'

interface ValuesInterface {
  company: any
  paymentMethods: JsonInterface[]
  regional: {
    [key: string]: string
  }
  vat: number
}

interface JsonInterface {}

export function getSettings(existingSettings?: SettingsInterface, refresh = false): AsyncAction<SettingsInterface> {
  return actionCreatorAsync<SettingsInterface>('GET_SETTINGS', async () => {
    return existingSettings && !refresh ? Promise.resolve(existingSettings) : api.getSettings()
  })
}

export const updateValues = (values): AsyncAction<ValuesInterface> =>
  actionCreatorAsync<ValuesInterface>('UPDATE_VALUES', () => api.saveValues(values))

export const updateSettingsOnFirstLogin = () => {
  return async (dispatch, getState) => {
    const state: StateInterface = getState()
    const currenciesRaw = getCurrencies(state.config)
    let companyCurrency
    currenciesRaw.some((c) => {
      const foundCurrency = state.company.currencyId === c.id
      if (foundCurrency) {
        companyCurrency = c.shortName
      }
      return foundCurrency
    })
    const newSettings: SettingsInterface = Immutable.copy(state.settings)
    newSettings.models.forEach((model) => {
      model.fields.forEach((field) => {
        if (field.name === 'currency') {
          field.default = companyCurrency || 'USD'
        }
        if (field.name === 'language') {
          field.default = state.company?.language
        }
        if (field.name === 'title') {
          field.default = l(`${model.type}.DefaultTitle`)
        }
      })
    })
    if (['fi', 'sv', 'fr', 'de', 'en', 'nl'].includes(state.company?.language)) {
      newSettings.managedFields.UNITS = DEFAULT_UNITS[state.company?.language]
    }
    await api.updateSettings({ managedFields: newSettings?.managedFields, models: newSettings?.models })
    await dispatch(getSettings())
  }
}

export const updateInvoiceSettingsOnVATLiableChange = (isNotVATLiable) => {
  return async (dispatch, getState) => {
    // If a company is not VATLiable, then change also the invoice type to SALES_VATNOLIABLE in invoiceSettings
    // as well as remove vatPct column from default visible columns
    const { config, settings, company } = getState()
    const invoiceTypes = getInvoiceTypes(config, company.country, DocumentTypes.INVOICE)
    const defaultInvoiceType = invoiceTypes.find(
      ({ type }) => type === (isNotVATLiable ? 'SALES_VATNOLIABLE' : 'SALES'),
    )
    const invoiceModel = Immutable.copy(settings.models).find(({ type }) => type === DocumentTypes.INVOICE)
    invoiceModel.fields.find(({ name }) => name === 'type').default = String(defaultInvoiceType.id)
    const columnsField = invoiceModel.fields.find(({ name }) => name === 'columns')
    if (isNotVATLiable) {
      columnsField.default = defaultInvoiceType.fields.join(',')
    } else if (!columnsField.default.includes('vatPct')) {
      columnsField.default = defaultInvoiceType.fields.join(',')
    }
    await dispatch(updateModels(invoiceModel, DocumentTypes.INVOICE, false))
  }
}

export const updateSettings = (updatedValues) => {
  return async (dispatch, getState) => {
    const values = Object.assign({}, getState().settings.values, updatedValues)
    await api.updateSettings({ values })
    await dispatch(getSettings())
  }
}

export const updateModels = (updatedModel: ModelInterface, type: DocumentTypes, refetchAfter = true) => {
  return async (dispatch, getState: () => StateInterface): Promise<void> => {
    const models = getState().settings.models.map((model) => {
      if (model.type === type) {
        return updatedModel
      }
      return model
    })
    await api.updateSettings({ models })
    if (refetchAfter) {
      await dispatch(getSettings())
    }
  }
}

export const updateManagedFields = (updatedManagedFields) => {
  return async (dispatch, getState) => {
    const managedFields = Object.assign({}, getState().settings?.managedFields, updatedManagedFields)
    await api.updateSettings({ managedFields })
    await dispatch(getSettings())
  }
}

export const initializeSettings = (name: string, currency: string) => {
  return async (dispatch, getState) => {
    const { settings } = getState()
    const updatedSettings = Immutable.copy(settings)
    updatedSettings.values.company.tradeNames[0].name = name
    updatedSettings.models &&
      updatedSettings.models.forEach((m) => {
        m.fields &&
          m.fields.forEach((f) => {
            f.default = f.name === 'currency' ? currency : f.default
          })
      })
    await dispatch(
      actionCreatorAsync('INITIALIZE_SETTINGS', async () => {
        return await api.updateSettings(updatedSettings)
      }),
    )
  }
}

export const updateTinkPaymentStatus = (status: boolean) => {
  return async (dispatch, getState) => {
    const { settings } = getState()
    const updatedSettings = Immutable.copy(settings)
    updatedSettings.values.company.tinkPaymentEnabled = status
    const { values } = updatedSettings
    await api.updateSettings({ values })
    await postSubscriptionSettingsChanges({ settingsItem: 'tinkPaymentEnabled', settingsValue: status })
    await dispatch(getSettings())
    await dispatch(getSubscriptionSettings())
  }
}

// CashAccount is same as paymentAccount
export const updateCashAccountId = (accountId: string) => {
  return async (dispatch, getState) => {
    const { settings } = getState()
    const updatedSettings = Immutable.copy(settings)
    updatedSettings.values.company.cashAccountId = accountId
    const { values } = updatedSettings
    await api.updateSettings({ values })
    await dispatch(getSettings())
  }
}
